import type {PropertyDefinitionBase, PropertySourceDetails, PropertyValue} from '@github-ui/custom-properties-types'
import {isEmptyPropertyValue} from '@github-ui/custom-properties-types/helpers'
import {enterprisePath, ownerPath} from '@github-ui/paths'
import {KebabHorizontalIcon, ShieldLockIcon, SyncIcon, UndoIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, FormControl, IconButton, Link, Stack, Token, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import type {PropsWithChildren, ReactElement, ReactNode, RefCallback} from 'react'

import {getReadOnlyValue} from '../../helpers/values-table'
import type {PropertyField} from '../../hooks/use-edit-custom-properties'
import {CustomPropertyInput} from '../CustomPropertyInput'
import styles from './PropertiesValuesTableRows.module.css'

interface EditValueRowProps<T extends PropertyDefinitionBase> {
  propertyField: PropertyField | undefined
  definition: T
  showUndo?: boolean
  fetchAllowedValuesUrl?: (propertyName: string) => string
  onChange: (name: string, value: PropertyValue) => void
  onReset: (name: string) => void
  required: boolean
  setRef?: RefCallback<HTMLElement>
}

export function EditValueRow<T extends PropertyDefinitionBase>(props: EditValueRowProps<T>) {
  const {propertyField, definition, showUndo, fetchAllowedValuesUrl, setRef, required} = props
  const {value, changed = false, mixed = false, error} = propertyField || {}
  const {propertyName, description} = definition

  const propertyNameValidationId = `${propertyName}-validation-error`

  // This is not strictly necessary, but it simplifies consuming it
  const onChange = (newValue: PropertyValue) => props.onChange(propertyName, newValue)
  const onReset = () => props.onReset(propertyName)

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        label={propertyName}
        description={description}
        required={required}
        value={
          <>
            <div className={styles.controlsContainer}>
              <CustomPropertyInput
                setRef={setRef}
                {...definition}
                propertyValue={value}
                mixed={mixed}
                onChange={onChange}
                inputProps={error ? {'aria-describedby': propertyNameValidationId} : {}}
                fetchAllowedValuesUrl={fetchAllowedValuesUrl}
              />
            </div>
            {error && (
              <FormControl.Validation
                variant="error"
                id={propertyNameValidationId}
                className={styles.FormControl_Validation}
              >
                {error}
              </FormControl.Validation>
            )}
          </>
        }
        trailing={<ResetDropdown definition={definition} {...{changed, onReset, onChange, showUndo}} />}
      />
    </PropertyValueRowContainer>
  )
}

EditValueRow.displayName = 'EditValueRow'

interface FormReadRowProps<T extends PropertyDefinitionBase> {
  definition: T
  propertyField: PropertyField | undefined
  required: boolean
}

export function FormReadValueRow<T extends PropertyDefinitionBase>({
  propertyField,
  definition,
  required,
}: FormReadRowProps<T>) {
  const {description, propertyName} = definition
  const readOnlyValue = getReadOnlyValue(definition, propertyField?.value)

  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        required={required}
        label={propertyName}
        description={description}
        value={
          <Stack direction="horizontal" gap="condensed">
            <Stack.Item shrink={false}>
              <ShieldLockIcon size="small" />
            </Stack.Item>
            <Stack.Item>
              <span>{readOnlyValue}</span>
            </Stack.Item>
          </Stack>
        }
      />
    </PropertyValueRowContainer>
  )
}

interface ReadRowProps<T extends PropertyDefinitionBase> {
  definition: T
  propertyField: PropertyField | undefined
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
  required: boolean
}

export function ReadValueRow<T extends PropertyDefinitionBase>({
  propertyField,
  definition,
  managedBySource,
  required,
}: ReadRowProps<T>) {
  const {description} = definition
  return (
    <PropertyValueRowContainer className={styles.tableRow}>
      <DataTableRow
        required={required}
        label={definition.propertyName}
        description={description}
        value={
          <DisplayPropertyValue
            propertyValue={propertyField?.value || definition.defaultValue || ''}
            definition={definition}
          />
        }
        bottomMessage={
          managedBySource && (
            <div className="d-inline">
              <span className="fg-muted">
                <ShieldLockIcon size={'small'} /> Managed by{' '}
              </span>
              <Link
                inline
                href={
                  managedBySource.type === 'org'
                    ? ownerPath({owner: managedBySource.slug})
                    : enterprisePath({slug: managedBySource.slug})
                }
              >
                {managedBySource.name}
              </Link>
            </div>
          )
        }
      />
    </PropertyValueRowContainer>
  )
}

function DisplayPropertyValue({
  propertyValue,
  definition,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinitionBase
}) {
  if (isEmptyPropertyValue(propertyValue)) {
    return <span className="fg-muted">(Empty)</span>
  }

  switch (definition.valueType) {
    case 'single_select':
    case 'true_false':
      return (
        <Token
          text={
            <Truncate title={propertyValue as string} maxWidth="100%">
              {propertyValue}
            </Truncate>
          }
          className={styles.propertyToken}
        />
      )
    case 'multi_select':
      return (
        <>
          {(propertyValue as string[]).map(v => (
            <Token
              key={v}
              text={
                <Truncate title={v} maxWidth="100%">
                  {v}
                </Truncate>
              }
              className={styles.spacedPropertyToken}
            />
          ))}
        </>
      )
    case 'string':
    case 'url':
      return (
        <Truncate title={propertyValue as string} maxWidth="100%">
          {propertyValue}
        </Truncate>
      )
  }
}

function ResetDropdown({
  definition,
  changed,
  showUndo = true,
  onReset,
  onChange,
}: {
  definition: PropertyDefinitionBase
  changed: boolean
  showUndo?: boolean
  onReset(): void
  onChange: (value: string) => void
}) {
  const {propertyName, required, source} = definition
  const undoTitle = `Undo ${propertyName} changes`
  const resetToDefaultTitle = `Reset ${propertyName} to default value`

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label={`Open reset options for ${propertyName}`}
          description="Reset options"
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="small">
        <ActionList>
          {showUndo && (
            <ActionList.Item
              onSelect={onReset}
              aria-label={undoTitle}
              inactiveText={changed ? undefined : 'No change to undo'}
            >
              <ActionList.LeadingVisual>
                <UndoIcon />
              </ActionList.LeadingVisual>
              <span className={styles.propertyLabel}>Undo</span>
              <span className={styles.propertyDescription}>Reset to the last used value</span>
            </ActionList.Item>
          )}
          {required && (
            <ActionList.Item onSelect={() => onChange('')} aria-label={resetToDefaultTitle}>
              <ActionList.LeadingVisual>
                <SyncIcon />
              </ActionList.LeadingVisual>
              <span className={styles.propertyLabel}>Reset to default</span>
              <span className={styles.propertyDescription}>
                {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
                Inherit the value defined by the {source.type === 'business' ? 'enterprise' : 'organization'}
              </span>
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function PropertyValueRowContainer(props: PropsWithChildren<{className?: string}>) {
  return (
    <div data-testid="property-row" className={clsx(styles.propertyRow, props.className)}>
      {props.children}
    </div>
  )
}

function DataTableRow({
  label,
  description,
  bottomMessage,
  value,
  trailing,
  required,
}: {
  label: string
  description: string | null
  bottomMessage?: ReactNode
  value: ReactNode
  trailing?: ReactNode
  required: boolean
}) {
  return (
    <FormControl required={required} className={clsx(styles.FormControl, bottomMessage && styles.hasBottomMessage)}>
      <FormControl.Label data-testid="property-name" className={styles.FormControl_Label}>
        {label}
      </FormControl.Label>
      <div className={styles.formInputArea}>{value}</div>
      {trailing ? <div className={styles.actionButtonsArea}>{trailing}</div> : null}
      {description && <FormControl.Caption className={styles.FormControl_Caption}>{description}</FormControl.Caption>}
      {bottomMessage ? <div className={styles.formMessageArea}>{bottomMessage}</div> : null}
    </FormControl>
  )
}

try{ FormReadValueRow.displayName ||= 'FormReadValueRow' } catch {}
try{ ReadValueRow.displayName ||= 'ReadValueRow' } catch {}
try{ DisplayPropertyValue.displayName ||= 'DisplayPropertyValue' } catch {}
try{ ResetDropdown.displayName ||= 'ResetDropdown' } catch {}
try{ PropertyValueRowContainer.displayName ||= 'PropertyValueRowContainer' } catch {}
try{ DataTableRow.displayName ||= 'DataTableRow' } catch {}